import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { Autocomplete, AutocompleteInputChangeReason, FilterOptionsState, TextField } from '@mui/material';
import { AppInputCustomProps, CustomFieldProps } from './types';
import useDebouncer from '@hooks/useDebounce';
import DisabledTooltip from '@components/DisabledTooltip';
import FormLabel from './FormLabel';
import classNames from 'classnames/bind';

import styles from './Form.module.css';
import { offWhite } from '@/Infrastructure/Theme/AppThemeOverrides';

const classNamesCtx = classNames.bind(styles);

/**
 * Props shared by the application Autocomplete inputs that say nothing about where the value is stored. Formik
 * state reaches these components as plain props (value/onValueChange plus the customProps flags), the same way
 * TextField and NumberField are arranged, so either can be used outside a Formik form.
 */
interface AppAutocompleteSharedProps extends AppInputCustomProps {
  disabled?: boolean;
  className?: string;
  disabledTooltipClass?: string;
  placeholder?: string;
  variant?: 'outlined' | 'standard' | 'filled';
  isLoading?: boolean;
  /** Callback method to do something else with the changed value */
  onChangeExtension?: (value?: unknown) => void;
}

export interface AppAutocompleteProps extends AppAutocompleteSharedProps {
  /** Controlled input value */
  value: string;
  onValueChange: (value: string) => void;
  autoCompleteItems?: string[];
  freeSolo?: boolean;
  onBlurExtension?: (value?: unknown) => void;
}

/**
 * Wrapper around MUI Autocomplete to provide application-wide default behaviors for a list of string options.
 */
export function AppAutocomplete({ customProps, ...props }: AppAutocompleteProps) {
  const autoCompleteClasses = classNamesCtx({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: props.disabled,
    [props.className ?? '']: Boolean(props.className)
  });

  const onInputBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    /**
     * Outside of freeSolo the user has to land on an actual option, so text left in the input that is not one of
     * them is discarded on blur. Judged against the value on screen rather than one committed elsewhere, so a
     * selection made moments earlier cannot be thrown away by a write that has not landed yet.
     */
    if (!props.freeSolo && props.value && !props.autoCompleteItems?.includes(props.value)) {
      props.onValueChange('');
      return;
    }

    props.onBlurExtension?.(event);
  };

  return (
    <DisabledTooltip
      message={props.disabled ? customProps.disabledReason : undefined}
      className={customProps.disabledReason ? autoCompleteClasses : props.disabledTooltipClass}>
      <Autocomplete
        loading={props.isLoading}
        freeSolo={props.freeSolo}
        onBlur={onInputBlurHandler}
        options={props.autoCompleteItems ?? []}
        value={props.value}
        disabled={props.disabled}
        sx={{ width: customProps.disabledReason ? '100%' : undefined }}
        disableClearable={!props.value}
        onInputChange={(_event?: SyntheticEvent, newValue?: string, reason?: string) => {
          if (reason === 'clear') {
            props.onValueChange('');
            return;
          }

          /** Event occurring while the user is typing. In freeSolo, this updates the value. */
          if (props.freeSolo) {
            props.onValueChange(newValue ?? '');
          }

          /** Callback when the value has changed */
          if (props.onChangeExtension && _event && _event.type === 'change') {
            props.onChangeExtension(newValue);
          }
        }}
        onChange={(_, newValue) => {
          if (!props.freeSolo) {
            props.onValueChange(newValue ?? '');
          }
        }}
        className={autoCompleteClasses}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                slotProps: {
                  root: {
                    className: classNamesCtx({
                      root: true,
                      autocomplete: true,
                      standard: props.variant === 'standard'
                    })
                  }
                }
              }
            }}
            variant={props.variant ?? 'outlined'}
            label={
              <FormLabel
                text={customProps.labelText}
                error={customProps.isError}
                errorText={customProps.errorText}
                required={customProps.required}
              />
            }
            placeholder={props.placeholder}
            error={customProps.isError}
            disabled={props.disabled}
          />
        )}
      />
    </DisabledTooltip>
  );
}

function asAutoCompleteInput() {
  return ({ field, form, ...props }: CustomFieldProps) => {
    const { value, initialValue, error, touched } = form.getFieldMeta<string>(field.name);

    const { dirty, validateForm } = form;

    const isDirty = useMemo(() => {
      if (dirty) {
        return value !== initialValue;
      }
      return false;
    }, [value, initialValue, dirty]);

    const isError: boolean = useMemo(() => {
      return Boolean(error && touched);
    }, [error, touched]);

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState<string>(value);

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      setMyValue((state) => {
        if (value !== state) {
          return value;
        }
        return state;
      });
      void validateForm();
    }, [value, validateForm]);

    /** When the local state has changed, compare to the form value. After 1/2 a second update the form if the value differs. */
    useDebouncer(
      () => {
        if (myValue !== value) {
          void form.setFieldTouched(field.name, false);
          void form.setFieldValue(field.name, myValue, true);
          void validateForm();
        }
      },
      [myValue],
      100
    );

    return (
      <AppAutocomplete
        value={myValue}
        onValueChange={setMyValue}
        autoCompleteItems={props.autoCompleteItems}
        freeSolo={props.freeSolo}
        isLoading={props.isLoading}
        placeholder={props.placeholder}
        variant={props.variant}
        className={props.className}
        disabledTooltipClass={props.disabledTooltipClass}
        onBlurExtension={props.onBlurExtension}
        onChangeExtension={props.onChangeExtension}
        disabled={form.isSubmitting || props.disabled}
        customProps={{
          labelText: props.label ?? '',
          isDirty,
          isError,
          errorText: error,
          required: props.required,
          disabledReason: props.disabledReason
        }}
      />
    );
  };
}

export const FormikAutocomplete = asAutoCompleteInput();

/**
 *	Validating that the ComplexAutoCompleteInput Formik Field received the required
 *	Props that it needs to function correctly since they are nullish on the parent interface.
 */
export function validateComplexAutoCompleteProps(props: CustomFieldProps): props is CustomFieldProps & {
  autoCompleteComplexItems: unknown[];
  getOptionKey: (option?: unknown) => string | null;
  getOptionLabel: (option: unknown) => string;
  getOptionDisabled?: (option: unknown) => boolean;
  isOptionEqualToValue: (option: unknown, value: unknown) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: unknown;
    },
    value: unknown
  ) => ReactNode;
} {
  return Boolean(props.autoCompleteComplexItems) && Boolean(props.getOptionKey) && Boolean(props.getOptionLabel);
}

export interface AppComplexAutocompleteProps extends AppAutocompleteSharedProps {
  /** Controlled selection. The option object itself, not its key. */
  value: unknown;
  onValueChange: (value: unknown) => void;
  autoCompleteComplexItems: unknown[];
  getOptionLabel: (option: unknown) => string;
  getOptionDisabled?: (option: unknown) => boolean;
  isOptionEqualToValue?: (option: unknown, value: unknown) => boolean;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: unknown;
    },
    value: unknown
  ) => ReactNode;
  /** Overrides MUI's option filtering, e.g. to cap the number of rendered options for large lists */
  filterOptions?: (options: unknown[], state: FilterOptionsState<unknown>) => unknown[];
  /** Called as the input text changes. The reason distinguishes typing from MUI replaying a selection's label. */
  onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  onBlur?: () => void;
  /** Overrides the default empty-list text, e.g. to state how many characters a search needs */
  noOptionsText?: string;
  popperClass?: string;
  rootClass?: string;
}

/**
 * Wrapper around MUI Autocomplete for choosing a complex object rather than a plain string option.
 */
export function AppComplexAutocomplete({ customProps, ...props }: AppComplexAutocompleteProps) {
  /** Track the input text separately to determine if user has typed anything */
  const [inputText, setInputText] = useState('');

  const { value } = props;
  const defaultOptionWhenValuePresent = useMemo(() => (value ? [value] : []), [value]);

  const autoCompleteClasses = classNamesCtx({
    input: true,
    dirty: customProps.isDirty,
    error: customProps.isError,
    disabled: props.disabled,
    [props.className ?? '']: Boolean(props.className)
  });

  return (
    <Autocomplete
      loading={props.isLoading}
      options={props.autoCompleteComplexItems.length > 0 ? props.autoCompleteComplexItems : defaultOptionWhenValuePresent}
      getOptionLabel={props.getOptionLabel}
      filterOptions={props.filterOptions}
      value={value}
      disableClearable={!value}
      popupIcon={props.disabled ? null : undefined}
      onInputChange={(_, text: string, reason) => {
        if (reason === 'clear') {
          props.onValueChange(null);
          setInputText('');
          return;
        }

        setInputText(text);
        props.onInputChange?.(text, reason);
      }}
      onChange={(_, newValue: unknown) => {
        props.onValueChange(newValue ?? null);
        props.onChangeExtension?.(newValue ?? null);
      }}
      onBlur={props.onBlur}
      getOptionDisabled={props.getOptionDisabled}
      isOptionEqualToValue={props.isOptionEqualToValue}
      disabled={props.disabled}
      className={autoCompleteClasses}
      blurOnSelect
      noOptionsText={props.noOptionsText ?? (!inputText ? 'Start Typing to Search' : 'No Options Found')}
      renderInput={(params) => (
        <TextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              autoComplete: 'new-password',
              slotProps: {
                root: {
                  className: classNamesCtx({
                    root: true,
                    autocomplete: true,
                    standard: props.variant === 'standard',
                    [props.rootClass ?? '']: Boolean(props.rootClass)
                  })
                }
              }
            }
          }}
          variant={props.variant ?? 'outlined'}
          label={
            <FormLabel
              text={customProps.labelText}
              error={customProps.isError}
              errorText={customProps.errorText}
              required={customProps.required}
            />
          }
          placeholder={props.placeholder}
          error={customProps.isError}
          ref={params.slotProps.input.ref}
          sx={{
            '& .MuiAutocomplete-endAdornment .MuiAutocomplete-clearIndicator': {
              backgroundColor: offWhite
            }
          }}
        />
      )}
      renderOption={props.renderOption}
      slotProps={{
        popper: {
          className: props.popperClass,
          placement: 'bottom-start'
        }
      }}
    />
  );
}

/**
 *	AutoComplete wrapper component for utilizing complex types instead of strings values with the AutoComplete Component
 */
function asComplexAutoCompleteInput() {
  return (fieldProps: CustomFieldProps) => {
    if (!validateComplexAutoCompleteProps(fieldProps)) throw new Error('Invalid ComplexAutoComplete Props');

    const { field, form, getOptionKey, autoCompleteComplexItems, ...props } = fieldProps;

    const { dirty, getFieldMeta } = form;

    /** Value represents the Formik value for the given field name. This is going to be
     * the id and not the complex object of the Autocomplete input.
     */
    const { value, initialValue, error, touched } = getFieldMeta<unknown>(field.name);

    const isDirty = useMemo(() => {
      if (dirty) {
        if (typeof value === 'object') {
          const valueKey = getOptionKey(value);
          const initKey = getOptionKey(initialValue);
          return valueKey !== initKey;
        }
        return value !== initialValue;
      }
      return false;
    }, [value, initialValue, dirty, getOptionKey]);

    const isError: boolean = useMemo(() => {
      return Boolean(error && touched);
    }, [error, touched]);

    const isDisabled = form.isSubmitting || props.disabled;

    /** Keeping track of the input state locally for performance reasons when typing into the input */
    const [myValue, setMyValue] = useState(() => {
      if (value) {
        /** Try to match the form value to the collection object to obtain all properties else default to what we have */
        return (
          autoCompleteComplexItems.find((x) => {
            return getOptionKey(x) === value;
          }) || value
        );
      }
      return null;
    });

    /** If the formik value changes outside of this component, update our local state to reflect */
    useEffect(() => {
      setMyValue((state: unknown) => {
        if (getOptionKey(value) !== getOptionKey(state)) {
          return autoCompleteComplexItems.find((x) => getOptionKey(x) === getOptionKey(value)) || value;
        }
        return state;
      });
    }, [value, getOptionKey, autoCompleteComplexItems]);

    /** When the local state has changed, compare to the form value. After 1/2 a second update the form if the value differs. */
    useDebouncer(
      () => {
        const inputValueKey = getOptionKey(myValue);
        if (inputValueKey !== value) {
          void form.setFieldTouched(field.name);
          void form.setFieldValue(field.name, myValue, true);
        }
      },
      [myValue],
      100
    );

    return (
      <AppComplexAutocomplete
        value={myValue}
        // Narrowed the way the previous inline onChange did, since the state was inferred from the matched option
        onValueChange={(next) => {
          setMyValue(next ?? null);
        }}
        autoCompleteComplexItems={autoCompleteComplexItems}
        getOptionLabel={props.getOptionLabel}
        getOptionDisabled={props.getOptionDisabled}
        isOptionEqualToValue={props.isOptionEqualToValue}
        renderOption={props.renderOption}
        filterOptions={props.filterOptions}
        onInputChange={props.onInputChange}
        onChangeExtension={props.onChangeExtension}
        onBlur={() => {
          void form.setFieldTouched(field.name, true);
        }}
        isLoading={props.isLoading}
        placeholder={props.placeholder}
        variant={props.variant}
        className={props.className}
        rootClass={props.rootClass}
        popperClass={props.popperClass}
        disabled={isDisabled}
        customProps={{
          labelText: props.label ?? '',
          isDirty,
          isError,
          errorText: error,
          required: props.required
        }}
      />
    );
  };
}

export const FormikComplexAutocomplete = asComplexAutoCompleteInput();
